import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, BookOpen, Database, Wrench, GitBranch, FolderPlus, Upload, Brain } from '@phosphor-icons/react';
import { Mic } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

const BrainActionModal = ({ isOpen, onClose, onActionSelect }) => {
  // Add React Router's useNavigate hook
  const navigate = useNavigate();

  // Animation variants
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.3 }
    },
    exit: { 
      opacity: 0,
      transition: { duration: 0.2 }
    }
  };

  const popupVariants = {
    hidden: { scale: 0.95, opacity: 0, y: 20 },
    visible: { 
      scale: 1, 
      opacity: 1, 
      y: 0,
      transition: { 
        type: "spring", 
        damping: 30, 
        stiffness: 300,
        duration: 0.4 
      }
    },
    exit: { 
      scale: 0.98, 
      opacity: 0,
      y: 10,
      transition: { duration: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (i) => ({ 
      opacity: 1, 
      y: 0,
      transition: { 
        delay: i * 0.05,
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }
    }),
    exit: { 
      opacity: 0,
      y: -10,
      transition: { duration: 0.2 }
    }
  };

  // Action types
  const actionTypes = [
    {
      id: 'simple_answer',
      title: 'Simple Answer',
      description: 'Provide a quick response to common customer questions',
      icon: BookOpen,
      color: 'bg-blue-100 text-blue-700'
    },
    {
      id: 'complex_answer',
      title: 'Complex Answer',
      description: 'Look up specific information like addresses or availability',
      icon: Database,
      color: 'bg-purple-100 text-purple-700'
    },
    {
      id: 'simple_action',
      title: 'Simple Action',
      description: 'Send a text, email, API, or transfer the call',
      icon: Wrench,
      color: 'bg-amber-100 text-amber-700'
    },
    {
      id: 'workflow',
      title: 'Workflow',
      description: 'Create multi-step decision tree conversations',
      icon: GitBranch,
      color: 'bg-green-100 text-green-700'
    },
    {
      id: 'create_category',
      title: 'New Category',
      description: "Organize your AI's knowledge with a new category",
      icon: FolderPlus,
      color: 'bg-yellow-100 text-yellow-700',
    }
  ];

  const handleActionClick = (actionType) => {
    // Special handling for complex_action - navigate to train/paths/order
    if (actionType === 'complex_action') {
      // First close the modal
      onClose();

      // Trigger the action select callback for Brain.js to handle opening the modal
      if (onActionSelect) {
        onActionSelect(actionType);
      }
      return;
    }

    // Handle 'create_category' or other action types
    if (onActionSelect) {
      onActionSelect(actionType);
    }
    onClose();
  };

  // Create the portal content
  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={backdropVariants}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[99999] p-4 overflow-hidden"
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
          onClick={onClose}
        >
          <motion.div
            variants={popupVariants}
            className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[85vh]"
            onClick={(e) => e.stopPropagation()}
            data-tutorial-target="brain-action-modal"
          >
            {/* Header */}
            <motion.div 
              className="flex items-center justify-between px-5 sm:px-7 py-4 sm:py-5 border-b sticky top-0 bg-white z-10"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.3 }}
            >
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="p-1.5 sm:p-2 bg-gray-100/80 rounded-lg">
                  <Brain size={16} className="text-gray-700 sm:w-[18px] sm:h-[18px]" />
                </div>
                <div>
                  <h2 className="font-medium text-base sm:text-lg text-gray-900">Add to Brain</h2>
                  <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5">
                    Select the type of content you want to add to your AI brain:
                  </p>
                </div>
              </div>

              <motion.button
                whileHover={{ scale: 1.05, backgroundColor: '#f3f4f6' }}
                whileTap={{ scale: 0.95 }}
                onClick={onClose}
                className="rounded-full h-7 w-7 sm:h-8 sm:w-8 flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors"
                aria-label="Close"
              >
                <X size={16} className="sm:w-[18px] sm:h-[18px]" />
              </motion.button>
            </motion.div>

            {/* Content */}
            <div className="p-5 sm:p-7 space-y-4 sm:space-y-5 overflow-y-auto flex-1">
              <motion.div 
                className="grid gap-3"
                initial="hidden"
                animate="visible"
                variants={{
                  visible: {
                    transition: {
                      staggerChildren: 0.07
                    }
                  }
                }}
              >
                <AnimatePresence>
                  {actionTypes.map((action, index) => (
                    <motion.button
                      key={action.id}
                      custom={index}
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      whileHover={{ 
                        scale: 1.01,
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)"
                      }}
                      whileTap={{ scale: 0.98 }}
                      className="flex items-center p-3 sm:p-4 border border-gray-100 rounded-xl hover:border-gray-200 hover:bg-gray-50 text-left transition-colors"
                      data-tutorial-action={action.id}
                      onClick={() => handleActionClick(action.id)}
                    >
                                             <div className={`p-2 rounded-lg ${action.color} mr-3 flex-shrink-0`}>
                        <action.icon size={16} className="sm:w-[18px] sm:h-[18px]" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900 text-xs sm:text-sm">{action.title}</h3>
                        <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5">{action.description}</p>
                      </div>
                    </motion.button>
                  ))}
                </AnimatePresence>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Render using portal
  return isOpen ? createPortal(modalContent, document.body) : null;
};

export default BrainActionModal; 